// Shared name-fallback for NPI-keyed physician rosters (Balance Health, Pacesetter, …). When a
// document has no usable NPI, the extracted "Family, Given" name can't match a roster display name
// directly, because extraction never captures suffixes ("Jr") or credentials ("DPM"). This matches
// the extracted name against the roster to recover the canonical display name. Pure — no I/O.

export type PhysicianNameIndexEntry = { familyName: string; givenName: string; physician: string };

// Builds a lowercased name index from a roster's display names, which are in
// "Family, Given[, credential]" format (e.g. "Weil Jr, Lowell, DPM"). Build once at module load.
export function buildPhysicianNameIndex(physicianNames: readonly string[]): PhysicianNameIndexEntry[] {
  return physicianNames.map((physician) => {
    const [familyName = "", givenName = ""] = physician.split(",").map((part) => part.trim().toLowerCase());

    return { familyName, givenName, physician };
  });
}

// Matches an extracted { familyName, givenName } against the roster index. Extracted names lack
// suffixes/credentials, so a family name matches if it equals or is a leading word of the roster's
// family field (so "Weil" matches "Weil Jr", but not "Benton-Weil"). Extraction can also carry a
// middle name the roster doesn't list, so a given name matches if it equals or has the roster's
// given name as a leading word (so extracted "George Armando" matches roster "George", but not
// "Georgetown"). Returns the canonical display name only when every match resolves to the same name
// (a provider listed under multiple NPIs yields duplicate entries with one display name — still
// unambiguous).
export function matchPhysicianByName(
  index: readonly PhysicianNameIndexEntry[],
  familyName: string | null | undefined,
  givenName: string | null | undefined
): string | null {
  const family = familyName?.trim().toLowerCase();
  const given = givenName?.trim().toLowerCase();

  if (!family || !given) return null;

  const matches = index.filter(
    (entry) =>
      (entry.givenName === given || given.startsWith(`${entry.givenName} `)) &&
      (entry.familyName === family || entry.familyName.startsWith(`${family} `))
  );

  const names = new Set(matches.map((entry) => entry.physician));

  return names.size === 1 ? [...names][0] : null;
}
